import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SpreadsheetViewer from './SpreadsheetViewer';
import { useAuth } from '../contexts/AuthContext';
import {
  uploadCloudFile,
  listCloudFiles,
  downloadCloudFile,
  deleteCloudFile,
} from '../lib/cloudFiles';
import FeedbackButton from './FeedbackButton';

// CSV parsing functions
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (insideQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        insideQuotes = !insideQuotes;
      }
    } else if (char === ',' && !insideQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
};

const parseCSV = (content) => {
  const lines = content.trim().split('\n');
  if (lines.length === 0) return { columns: [], rows: [] };

  const headers = parseCSVLine(lines[0]);
  const rows = lines.slice(1).map(line => {
    const values = parseCSVLine(line);
    const row = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    return row;
  });

  return { columns: headers, rows };
};

const parseSheet = (XLSX, worksheet) => {
  if (!worksheet) {
    return { columns: [], rows: [] };
  }

  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

  const headers = [];
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const cellAddress = XLSX.utils.encode_col(C) + '1';
    const cell = worksheet[cellAddress];
    headers.push(cell ? cell.v : '');
  }

  const rows = [];
  for (let R = range.s.r + 1; R <= range.e.r; ++R) {
    const row = {};
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];
      const colName = headers[C - range.s.c];
      row[colName] = cell ? (cell.v !== undefined ? cell.v : '') : '';
    }
    rows.push(row);
  }

  return { columns: headers.filter(h => h !== ''), rows };
};

const parseExcel = (content) => {
  try {
    const XLSX = window.XLSX || require('xlsx');
    const workbook = XLSX.read(content, { type: 'binary' });

    return workbook.SheetNames.map(name => {
      const { columns, rows } = parseSheet(XLSX, workbook.Sheets[name]);
      return { name, columns, rows };
    });
  } catch (err) {
    throw new Error(`Failed to parse Excel file: ${err.message}`);
  }
};

// Parse a Blob into sheets (reused for cloud file download)
const parseBlobToSheets = async (blob, name) => {
  const ext = name.toLowerCase().split('.').pop();

  if (ext === 'csv') {
    const text = await blob.text();
    const { columns, rows } = parseCSV(text);
    return [{ name, columns, rows }];
  } else if (ext === 'xlsx' || ext === 'xls') {
    const binary = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsBinaryString(blob);
    });
    return parseExcel(binary);
  }

  throw new Error('Unsupported file format. Please use CSV or XLSX.');
};

const CLOUD_ENABLED = false;

function AppShell() {
  const navigate = useNavigate();
  const { user, profile, isPremium, signOut } = useAuth();
  const [sheets, setSheets] = useState(null);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const fileInputRef = useRef(null);
  const currentFileRef = useRef(null);

  // Cloud state
  const [cloudFiles, setCloudFiles] = useState([]);
  const [activeCloudFileId, setActiveCloudFileId] = useState(null);
  const [cloudLoading, setCloudLoading] = useState(false);

  const data = sheets ? { columns: sheets[activeSheetIndex].columns, rows: sheets[activeSheetIndex].rows } : null;

  // Fetch cloud files on mount when user exists
  useEffect(() => {
    if (CLOUD_ENABLED && user) {
      setCloudLoading(true);
      listCloudFiles(user.id)
        .then(setCloudFiles)
        .catch((err) => console.error('Failed to load cloud files:', err))
        .finally(() => setCloudLoading(false));
    }
  }, [user]);

  const refreshCloudFiles = useCallback(async () => {
    if (!user) return;
    try {
      const files = await listCloudFiles(user.id);
      setCloudFiles(files);
    } catch (err) {
      console.error('Failed to refresh cloud files:', err);
    }
  }, [user]);

  const handleFileOpen = useCallback(async (file) => {
    setLoading(true);
    setError('');
    setSheets(null);
    setActiveSheetIndex(0);
    setActiveCloudFileId(null);
    currentFileRef.current = file;

    try {
      const ext = file.name.toLowerCase().split('.').pop();

      let fileContent;
      if (ext === 'csv') {
        fileContent = await file.text();
      } else if (ext === 'xlsx' || ext === 'xls') {
        const reader = new FileReader();
        fileContent = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsBinaryString(file);
        });
      } else {
        throw new Error('Unsupported file format. Please use CSV or XLSX.');
      }

      let parsedSheets;

      if (ext === 'csv') {
        const { columns, rows } = parseCSV(fileContent);
        parsedSheets = [{ name: file.name, columns, rows }];
      } else if (ext === 'xlsx' || ext === 'xls') {
        parsedSheets = parseExcel(fileContent);
      }

      if (!parsedSheets || parsedSheets.length === 0 || parsedSheets[0].columns.length === 0) {
        throw new Error('File appears to be empty or invalid.');
      }

      setSheets(parsedSheets);
      setFileName(file.name);
    } catch (err) {
      setError(err.message || 'Failed to load file');
      console.error('Error loading file:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSaveToCloud = useCallback(async () => {
    if (!user || !currentFileRef.current) return;
    setCloudLoading(true);
    try {
      const cloudFile = await uploadCloudFile(user.id, currentFileRef.current);
      await refreshCloudFiles();
      setActiveCloudFileId(cloudFile.id);
    } catch (err) {
      console.error('Failed to save to cloud:', err);
      setError('Failed to save file to cloud: ' + err.message);
    } finally {
      setCloudLoading(false);
    }
  }, [user, refreshCloudFiles]);

  const handleOpenCloudFile = useCallback(async (cloudFile) => {
    setLoading(true);
    setError('');
    setSheets(null);
    setActiveSheetIndex(0);
    currentFileRef.current = null;

    try {
      const blob = await downloadCloudFile(cloudFile.storage_path);
      const parsedSheets = await parseBlobToSheets(blob, cloudFile.file_name);

      if (!parsedSheets || parsedSheets.length === 0 || parsedSheets[0].columns.length === 0) {
        throw new Error('File appears to be empty or invalid.');
      }

      setSheets(parsedSheets);
      setFileName(cloudFile.file_name);
      setActiveCloudFileId(cloudFile.id);
    } catch (err) {
      setError(err.message || 'Failed to load cloud file');
      console.error('Error loading cloud file:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDeleteCloudFile = useCallback(async (fileId, storagePath) => {
    setCloudLoading(true);
    try {
      await deleteCloudFile(fileId, storagePath);
      await refreshCloudFiles();
      // Clear state if the deleted file was active
      if (activeCloudFileId === fileId) {
        setSheets(null);
        setFileName('');
        setActiveCloudFileId(null);
        currentFileRef.current = null;
      }
    } catch (err) {
      console.error('Failed to delete cloud file:', err);
      setError('Failed to delete cloud file: ' + err.message);
    } finally {
      setCloudLoading(false);
    }
  }, [activeCloudFileId, refreshCloudFiles]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileOpen(file);
    }
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileOpen(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleNewFile = () => {
    fileInputRef.current?.click();
  };

  const handleGoHome = () => {
    setSheets(null);
    setFileName('');
    setActiveSheetIndex(0);
    setActiveCloudFileId(null);
    currentFileRef.current = null;
    setError('');
  };

  if (data) {
    return (
      <div className="h-screen bg-cream-100 flex flex-col">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          className="hidden"
        />
        <SpreadsheetViewer
          data={data}
          fileName={fileName}
          sheets={sheets}
          activeSheetIndex={activeSheetIndex}
          onSheetChange={setActiveSheetIndex}
          onNewFile={handleNewFile}
          onGoHome={handleGoHome}
          onLogout={handleLogout}
          cloudFiles={CLOUD_ENABLED ? cloudFiles : []}
          activeCloudFileId={CLOUD_ENABLED ? activeCloudFileId : null}
          onSaveToCloud={handleSaveToCloud}
          onOpenCloudFile={handleOpenCloudFile}
          onDeleteCloudFile={handleDeleteCloudFile}
          cloudLoading={cloudLoading}
          user={CLOUD_ENABLED ? user : null}
          profile={profile}
          isPremium={isPremium}
        />
      </div>
    );
  }

  return (
    <div className="h-screen bg-cream-100 flex">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Sidebar */}
      {sidebarOpen && (
        <div className="w-64 bg-cream-100 border-r border-cream-300 flex flex-col flex-shrink-0">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-cream-300">
            <div className="flex items-center gap-2">
              <img src="/assets/logo_2.png" alt="QuickSheet" className="w-6 h-6 object-contain" />
              <span className="font-semibold text-navy-900 text-sm">QuickSheet</span>
            </div>
          </div>

          {/* Sidebar Content */}
          <div className="flex-1 overflow-y-auto p-3">
            {/* Open File Button */}
            <div className="mb-6">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2 mb-2">
                Local Files
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-cream-200 rounded transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Open File
              </button>
            </div>

            {/* Cloud Files Section */}
            {CLOUD_ENABLED && user && (
              <div className="mb-6">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2 mb-2">
                  Cloud Files
                </div>
                {cloudLoading && (
                  <div className="px-2 py-2 flex items-center gap-2 text-xs text-gray-400">
                    <div className="animate-spin h-3 w-3 border border-copper-500 border-t-transparent rounded-full"></div>
                    Loading...
                  </div>
                )}
                {!cloudLoading && cloudFiles.length === 0 && (
                  <div className="px-2 py-2 text-xs text-gray-400 italic">
                    No cloud files yet
                  </div>
                )}
                <div className="space-y-1">
                  {cloudFiles.map(cf => (
                    <div key={cf.id} className="group flex items-center justify-between px-2 py-1.5 hover:bg-cream-200 rounded transition-colors">
                      <button
                        onClick={() => handleOpenCloudFile(cf)}
                        className="flex-1 text-left text-sm text-gray-700 flex items-center gap-2 truncate"
                      >
                        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3-3m0 0l3 3m-3-3v12" />
                        </svg>
                        <span className="truncate">{cf.file_name}</span>
                      </button>
                      <button
                        onClick={() => handleDeleteCloudFile(cf.id, cf.storage_path)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-red-600 transition-all flex-shrink-0"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar Footer */}
          <div className="p-3 border-t border-cream-300 space-y-1">
            {/* User Info */}
            {profile && (
              <div className="flex items-center gap-2.5 px-2 py-2 mb-1">
                {profile.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt=""
                    className="w-7 h-7 rounded-full flex-shrink-0 object-cover"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-copper-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-semibold">
                      {(profile.full_name || profile.email || '?').charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="min-w-0">
                  {profile.full_name && (
                    <div className="text-sm font-medium text-navy-900 truncate">{profile.full_name}</div>
                  )}
                  <div className="text-xs text-gray-500 truncate">{profile.email}</div>
                </div>
              </div>
            )}

            {/* Collapse Sidebar Button */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="w-full text-left px-2 py-1.5 text-sm text-gray-600 hover:bg-cream-200 rounded transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
              Collapse
            </button>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full text-left px-2 py-1.5 text-sm text-gray-600 hover:bg-cream-200 rounded transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="border-b border-cream-300 bg-cream-50">
          <div className="px-6 py-3 flex items-center">
            {!sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-1.5 text-gray-500 hover:bg-cream-200 rounded transition-colors"
                title="Show sidebar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            <div className="ml-auto">
              <FeedbackButton user={user} />
            </div>
          </div>
        </div>

        {/* Upload Zone Content */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 overflow-auto">
          {/* Logo and Branding */}
          <div className="mb-16 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <img src="/assets/logo_2.png" alt="QuickSheet Logo" className="w-12 h-12 object-contain" />
              <h1 className="text-5xl font-serif font-bold text-navy-900">
                QuickSheet
              </h1>
            </div>
            <p className="text-lg text-gray-500 max-w-md mx-auto">
              A clean, beautiful way to view and analyze your spreadsheet data
            </p>
          </div>

          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
            className="w-full max-w-xl border-2 border-dashed border-cream-300 rounded-lg p-16 text-center hover:border-copper-400 hover:bg-cream-50 transition-all cursor-pointer bg-cream-50 group"
          >
            <div className="mb-6 flex justify-center">
              <div className="w-16 h-16 bg-cream-200 rounded-lg flex items-center justify-center group-hover:bg-cream-300 transition-colors">
                <svg className="w-8 h-8 text-copper-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-serif font-semibold text-navy-900 mb-2">
              Drop a file to upload
            </h2>
            <p className="text-gray-500 mb-3">
              or click to browse
            </p>
            <p className="text-sm text-gray-400">
              CSV, XLSX files supported
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="mt-8 flex items-center gap-3">
              <div className="animate-spin h-5 w-5 border-2 border-copper-500 border-t-transparent rounded-full"></div>
              <p className="text-gray-600">Loading file...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="mt-8 w-full max-w-xl bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium text-sm">Error</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Footer Info */}
          <div className="mt-16 text-center text-sm text-gray-400">
            <p>All data is processed locally in your browser</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppShell;
